// apps/client/src/pages/leads/edm/EdmTopicsPage.tsx
//
// /leads/edm/topics (plan-v3 core E2, `featureFlags.edm_preferences`): the
// company's email topics. Recipients choose which topics they receive on the
// preference page their unsubscribe link opens; each campaign names its topic.
//
// Company-wide callers only (RequireEdmImportAccess on the route; saveTopics
// re-checks isCompanyWideEdmCaller on the server). Reads and writes go only
// through edmCampaignAction getTopics / saveTopics (useEdmContent); no
// Firestore SDK reads.
//
// Topic ids are minted by the server and never change: a new topic is sent
// without an id, a renamed one keeps its id. Removing a topic keeps every
// opt-out already recorded against that topic id (opt-outs behave like
// suppressions); a topic added later gets a new id and starts with no
// opt-outs. The server refuses removal while an unfinished campaign uses it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { ListChecks, Plus, Save, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { SkeletonTablePage } from '../../../components/common/SkeletonLoader';
import { Spinner } from '../../../components/common/Spinner';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useEdmContent, type EdmTopic } from '../../../hooks/useEdmContent';
import { EDM_UI, edmErrorMessage, edmErrorReason, isEdmPreferencesEnabled } from '../../../utils/edm';

/** Server ceilings mirrored so the page never sends what saveTopics refuses. */
export const EDM_TOPICS_MAX = 10;
export const EDM_TOPIC_NAME_MAX = 60;

/** A row being edited. `id` null = new (the server mints its id on save). */
interface DraftTopic {
  key: string;
  id: string | null;
  name: string;
}

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
  companyData?: { featureFlags?: Record<string, unknown> | null } | null;
}

/** Topic refusals first, then the shared eDM copy. */
export function edmTopicErrorMessage(err: unknown): string {
  if (edmErrorReason(err) === 'topic_in_use') {
    return 'A campaign that has not finished uses a topic you removed. Change or cancel that campaign first, then save again.';
  }
  return edmErrorMessage(err);
}

/** The first problem with the draft list, or null when it can be saved. */
export function topicDraftError(rows: ReadonlyArray<{ name: string }>): string | null {
  if (rows.length > EDM_TOPICS_MAX) return `You can have up to ${EDM_TOPICS_MAX} topics.`;
  const seen = new Set<string>();
  for (const r of rows) {
    const name = r.name.trim();
    if (name.length === 0) return 'Every topic needs a name.';
    if (name.length > EDM_TOPIC_NAME_MAX) return `Topic names are up to ${EDM_TOPIC_NAME_MAX} characters.`;
    const norm = name.toLocaleLowerCase('en-AU');
    if (seen.has(norm)) return `"${name}" is listed twice. Topic names must be different.`;
    seen.add(norm);
  }
  return null;
}

const toDraft = (topics: EdmTopic[]): DraftTopic[] => topics.map((t) => ({ key: `id:${t.id}`, id: t.id, name: t.name }));

const sameList = (a: DraftTopic[], b: DraftTopic[]): boolean =>
  a.length === b.length && a.every((r, i) => r.id === b[i].id && r.name.trim() === b[i].name.trim());

const EdmTopicsPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx?.userData?.companyId;
  const enabled = isEdmPreferencesEnabled(ctx?.companyData?.featureFlags);
  const api = useEdmContent(companyId);

  const [saved, setSaved] = useState<DraftTopic[] | null>(null);
  const [rows, setRows] = useState<DraftTopic[]>([]);
  const [newName, setNewName] = useState('');
  const [newSeq, setNewSeq] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<DraftTopic | null>(null);

  const load = useCallback(() => {
    if (!enabled) return;
    if (!companyId) {
      setLoading(false);
      setError('Your company could not be loaded. Refresh the page.');
      return;
    }
    setLoading(true);
    setError(null);
    api
      .getTopics()
      .then((res) => {
        const draft = toDraft(res.topics);
        setSaved(draft);
        setRows(draft);
      })
      .catch((err) => setError(edmTopicErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [api, companyId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const draftError = useMemo(() => topicDraftError(rows), [rows]);
  const dirty = saved !== null && !sameList(rows, saved);

  if (!enabled) return <Navigate to="/leads/edm" replace />;
  if (loading && saved === null && !error) return <SkeletonTablePage tableRows={4} tableColumns={2} />;

  const trimmedNew = newName.trim();
  const canAdd = !saving && rows.length < EDM_TOPICS_MAX && trimmedNew.length > 0 && trimmedNew.length <= EDM_TOPIC_NAME_MAX;

  const addTopic = () => {
    if (!canAdd) return;
    setRows((r) => [...r, { key: `new:${newSeq}`, id: null, name: trimmedNew }]);
    setNewSeq((n) => n + 1);
    setNewName('');
    setNotice(null);
  };

  const rename = (key: string, name: string) => {
    setRows((r) => r.map((row) => (row.key === key ? { ...row, name } : row)));
    setNotice(null);
  };

  const removeRow = (row: DraftTopic) => {
    // A topic never saved has no opt-outs; drop it without asking.
    if (row.id === null) setRows((r) => r.filter((x) => x.key !== row.key));
    else setRemoveTarget(row);
    setNotice(null);
  };

  const confirmRemove = () => {
    const target = removeTarget;
    setRemoveTarget(null);
    if (target) setRows((r) => r.filter((x) => x.key !== target.key));
  };

  const save = () => {
    if (draftError || !dirty || saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    const topics = rows.map((r) => (r.id === null ? { name: r.name.trim() } : { id: r.id, name: r.name.trim() }));
    api
      .saveTopics(topics)
      .then((res) => {
        const draft = toDraft(Array.isArray(res?.topics) ? res.topics : []);
        setSaved(draft);
        setRows(draft);
        setNotice('Topics saved.');
      })
      .catch((err) => setError(edmTopicErrorMessage(err)))
      .finally(() => setSaving(false));
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        icon={ListChecks}
        title="Email topics"
        subtitle="The kinds of email you send, so recipients can choose what they receive"
        // Refresh would replace unsaved edits with the saved list; it is
        // offered only when there is nothing unsaved.
        onRefresh={dirty ? undefined : load}
        isRefreshing={loading}
        actions={
          <button type="button" className={EDM_UI.primaryButton} disabled={!dirty || !!draftError || saving} onClick={save}>
            {saving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />} Save topics
          </button>
        }
      />

      {notice && <div className={EDM_UI.infoBanner}>{notice}</div>}
      {error && <div className={EDM_UI.errorBanner}>{error}</div>}

      {/* Editing needs the saved list: without it a save would overwrite
          topics this page never loaded. */}
      {saved !== null && (
        <div className={EDM_UI.card}>
          <p className="text-sm text-gray-400">
            Each campaign is sent under one topic. The unsubscribe link in every email opens a page where the recipient
            can stop one topic or unsubscribe from everything. Someone who stops a topic is never sent that topic again.
            Removing a topic keeps those choices on record, but a topic you add later is a new topic that nobody has
            stopped yet. You can have up to {EDM_TOPICS_MAX} topics.
          </p>

          {rows.length === 0 ? (
            <p className="text-sm text-gray-300">No topics yet. Add your first one below.</p>
          ) : (
            <ul className="divide-y divide-gray-700 rounded-lg border border-gray-700" aria-label="Topics">
              {rows.map((row, i) => (
                <li key={row.key} className="flex items-center gap-3 px-4 py-2">
                  <label className="sr-only" htmlFor={`edm-topic-${i}`}>
                    Topic {i + 1} name
                  </label>
                  <input
                    id={`edm-topic-${i}`}
                    className={EDM_UI.input}
                    maxLength={EDM_TOPIC_NAME_MAX}
                    value={row.name}
                    disabled={saving}
                    onChange={(e) => rename(row.key, e.target.value)}
                  />
                  {row.id === null && <span className="whitespace-nowrap text-xs text-gray-400">Not saved</span>}
                  <button
                    type="button"
                    className="text-red-400 hover:text-red-300 disabled:opacity-50"
                    disabled={saving}
                    aria-label={`Remove topic ${row.name.trim() || i + 1}`}
                    onClick={() => removeRow(row)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            aria-label="Add a topic"
            onSubmit={(e) => {
              e.preventDefault();
              addTopic();
            }}
          >
            <div className="flex-1">
              <label className={EDM_UI.label} htmlFor="edm-topic-new">
                New topic
              </label>
              <input
                id="edm-topic-new"
                className={EDM_UI.input}
                maxLength={EDM_TOPIC_NAME_MAX}
                placeholder="For example: Rate updates"
                value={newName}
                disabled={saving || rows.length >= EDM_TOPICS_MAX}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <button type="submit" className={EDM_UI.secondaryButton} disabled={!canAdd}>
              <Plus className="h-4 w-4" /> Add topic
            </button>
          </form>

          {draftError && <div className={EDM_UI.warnBanner}>{draftError}</div>}
          {dirty && !draftError && <p className="text-xs text-gray-400">You have unsaved changes.</p>}
        </div>
      )}

      <ConfirmDialog
        isOpen={removeTarget !== null}
        title="Remove topic"
        message={`Remove the topic "${removeTarget?.name.trim() ?? ''}"? People who already stopped this topic keep that choice on record. A campaign that has not finished and uses this topic must be changed first. The removal takes effect when you save.`}
        confirmLabel="Remove topic"
        confirmVariant="danger"
        onConfirm={confirmRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};

export default EdmTopicsPage;
